/**
 * HrChildModal — add or edit a member's child (name + date of birth).
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { hrService } from '../../../services/hr.service';
import { useToast } from '../../../hooks/useToast';
import { handleApiError } from '../../../lib/axios';
import type { MemberChild } from '../../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  memberId: number;
  /** When set, the modal edits this child; otherwise it adds a new one. */
  child?: MemberChild | null;
}

export function HrChildModal({ isOpen, onClose, memberId, child }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(child?.name ?? '');
    setDob(child?.date_of_birth ?? '');
  }, [isOpen, child]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), date_of_birth: dob || null };
      return child
        ? hrService.updateChild(memberId, child.id, payload)
        : hrService.addChild(memberId, payload);
    },
    onSuccess: () => {
      toast(child ? 'Child updated' : 'Child added', 'success');
      queryClient.invalidateQueries({ queryKey: ['member-children', memberId] });
      onClose();
    },
    onError: (err) => toast(handleApiError(err), 'error'),
  });

  const canSave = name.trim() !== '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={child ? 'Edit Child' : 'Add Child'} size="sm">
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSave) mutation.mutate(); }}
        className="space-y-4"
      >
        <Input label="Name" required value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Date of Birth" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={mutation.isPending} disabled={!canSave}>
            {child ? 'Save' : 'Add'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
